import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { List } from 'immutable';
import moment from 'moment';

import { getCalendar } from 'flow/actions/extension-actions';
import { toggleAutomeeting as toggleAutomeetingAction } from 'flow/actions/profile-actions';
import {
    changeSocialIntegrationSettings,
    externalSyncIsDone,
    fetchSocialIntegrationSettings,
    socialIntegrationSignOut,
    stopSettingsPollTimeout,
} from 'flow/actions/social-integration-actions';
import SettingsBlock from 'flow/components/settings/SettingsBlock';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import { selectSocialIntegrationSettings } from 'flow/selectors/social-integration-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { canUseExternalCalendar } from 'flow/utils/calendar-utils';
import { Capabilities } from 'flow/utils/capability-utils';
import { generatePath } from 'flow/utils/router-utils';
import {
    createReturnUrl,
    getIcon,
    getNbrSyncedContactsDescription,
    goToExternalSignIn,
    hasCalendarSync,
    synchronisationEnabled,
} from 'flow/utils/social-integration-utils';
import { twMerge } from 'tailwind-merge';
import Page from './Page';
import SettingRow from './SettingRow';
import SettingSection from './SettingSection';

const FETCH_CALENDAR_START_TIME = 100;
const delay = 5000;
const FETCH_CALENDAR_MAX_TIME = 10000;

const SourceConfiguration = {
    google: {
        title: 'settings.social_integration.synchronisation.google',
        calendarSync: Capabilities.GOOGLE_CALENDAR_SYNC,
        contactSync: Capabilities.GOOGLE_CONTACT_SYNC,
        cloudSync: false,
        workHourSync: false,
    },
    microsoft: {
        title: 'settings.social_integration.synchronisation.microsoft',
        calendarSync: Capabilities.MICROSOFT_CALENDAR_SYNC,
        contactSync: Capabilities.MICROSOFT_CONTACT_SYNC,
        cloudSync: false,
        workHourSync: true,
    },
};

const SocialIntegrationSynchronisation = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { extensionKey, syncSource } = useParams<{
        extensionKey: ExtensionKey;
        syncSource: keyof typeof SourceConfiguration;
    }>();

    const hasCapabilityContactSync = useAppSelector((state) =>
        selectHasCapability(
            state,
            extensionKey,
            SourceConfiguration[syncSource].contactSync,
        ),
    );

    const hasCapabilityCalendarSync = useAppSelector((state) =>
        selectHasCapability(
            state,
            extensionKey,
            SourceConfiguration[syncSource].calendarSync,
        ),
    );

    const socialIntegrationSettingValues = useAppSelector(
        selectSocialIntegrationSettings,
    );

    const socialIntegrationSettings = useAppSelector((state) =>
        state.socialIntegration.getIn([
            'socialIntegrationSettings',
            syncSource,
        ]),
    );

    const syncIsDone = useAppSelector((state) =>
        state.socialIntegration.getIn(
            ['socialIntegrationSettings', 'externalSignInFinished'],
            true,
        ),
    );

    const calendar = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey, 'calendar']),
    );

    const [isSyncing, setIsSyncing] = useState(false);
    const [numberOfFetches, setNumberOfFetches] = useState(0);
    const [syncStartTime, setSyncStartTime] = useState(undefined);
    const [fetchSettingsInterval, setFetchSettingsInterval] =
        useState(undefined);

    const [fetchCalendarTime, setFetchCalendarTime] = useState(
        FETCH_CALENDAR_START_TIME,
    );

    const [fetchCalendarTimeOut, setFetchCalendarTimeOut] = useState<
        NodeJS.Timeout | number
    >();

    const calendarToggled = () =>
        socialIntegrationSettings.get('syncTypes').includes('calendar');

    const fetchCalendarIfNeeded = () => {
        if (!socialIntegrationSettings || !calendar) {
            return;
        }

        if (calendarToggled() && !calendar.has('url')) {
            setFetchCalendarTime(
                fetchCalendarTime >= FETCH_CALENDAR_MAX_TIME
                    ? FETCH_CALENDAR_MAX_TIME
                    : fetchCalendarTime * 2,
            );

            clearTimeout(fetchCalendarTimeOut as NodeJS.Timeout);

            setFetchCalendarTimeOut(
                setTimeout(() => {
                    dispatch(getCalendar(extensionKey));
                }, fetchCalendarTime),
            );
        }
    };

    useEffect(() => {
        fetchCalendarIfNeeded();
        return () => {
            stopSettingsPollTimeout();
        };
    }, []);

    useEffect(() => {
        if (isSyncing && !fetchSettingsInterval) {
            setSyncStartTime(Date.now());

            try {
                dispatch(fetchSocialIntegrationSettings());
            } catch (error) {
                console.error(
                    'Could not fetch social integration settings',
                    error,
                );
            }

            setFetchSettingsInterval(
                setInterval(() => {
                    try {
                        dispatch(fetchSocialIntegrationSettings());
                    } catch (error) {
                        console.error(
                            'Could not fetch social intregration settings',
                            error,
                        );
                    }

                    setNumberOfFetches((prev) => prev + 1);

                    if (numberOfFetches > numberOfFetches) {
                        stopFetchSettingsInterval();
                    }
                }, delay),
            );
        }

        if (isSyncing && socialIntegrationSettings) {
            const lastSyncDateTime =
                socialIntegrationSettings.get('lastSyncString');
            const lastSyncFailed =
                lastSyncFailedUnknownError() ||
                lastSyncFailedUnsupportedAccount();

            if (
                (lastSyncDateTime &&
                    syncStartTime < new Date(lastSyncDateTime).getTime()) ||
                lastSyncFailed
            ) {
                stopFetchSettingsInterval();
            }
        }
        return () => {
            if (fetchSettingsInterval) {
                clearInterval(fetchSettingsInterval);
            }
        };
    }, [isSyncing, socialIntegrationSettings]);

    if (!socialIntegrationSettings || !calendar) {
        return <Loading />;
    }

    const lastSyncFailedUnknownError = () =>
        socialIntegrationSettings
            .get('syncStatus')
            // @ts-expect-error
            .some((value) => value.includes('error'));

    const lastSyncFailedUnsupportedAccount = () =>
        socialIntegrationSettings
            .get('syncStatus')
            // @ts-expect-error
            .includes('unsupported_account');

    const stopFetchSettingsInterval = () => {
        if (fetchSettingsInterval) {
            clearInterval(fetchSettingsInterval);
            setFetchSettingsInterval(undefined);
        }
        setNumberOfFetches(0);
        setSyncStartTime(undefined);
        setIsSyncing(false);
    };

    const signOut = async () => {
        await dispatch(socialIntegrationSignOut(syncSource));
        await dispatch(fetchSocialIntegrationSettings());
        history.push(
            generatePath(routes['settings.account'], { extensionKey }),
        );
    };

    const getLastSyncDescription = (
        lastSyncDateTime: string | null,
        synchronisationEnabled: boolean,
    ): string | null => {
        if (!lastSyncDateTime) {
            if (
                synchronisationEnabled &&
                !(
                    lastSyncFailedUnknownError() ||
                    lastSyncFailedUnsupportedAccount()
                )
            ) {
                return i18next.t(
                    'settings.social_integration.synchronisation.in_progress',
                );
            }
            return null;
        }

        if (!synchronisationEnabled) {
            return null;
        }

        return i18next.t(
            'settings.social_integration.synchronisation.specification',
            { lastSync: moment(lastSyncDateTime).fromNow() },
        );
    };

    const renderSynchronisationSpec = () => {
        const syncTypes = socialIntegrationSettings.get('syncTypes');
        const contactCount = socialIntegrationSettings.get('contactCount');

        const syncedContactsDescription = syncTypes.includes('contacts')
            ? getNbrSyncedContactsDescription(contactCount, i18next)
            : null;
        const syncedCalendarDescription = syncTypes.includes('calendar')
            ? i18next.t('settings.social_integration.one_calendar')
            : null;

        const nbrSyncedResourcesDescriptions = [
            syncedContactsDescription,
            syncedCalendarDescription,
        ]
            .filter((val) => val !== null)
            .join(', ');

        const lastSyncDateTime =
            socialIntegrationSettings.get('lastSyncString');
        const lastSyncDescription = getLastSyncDescription(
            lastSyncDateTime,
            synchronisationEnabled(syncTypes),
        );

        const synchronisationInProgress =
            synchronisationEnabled(syncTypes) && !lastSyncDateTime;

        return (
            <SettingsBlock>
                <div className="flex items-center gap-2">
                    {getIcon(syncSource)}
                    {!synchronisationInProgress &&
                        nbrSyncedResourcesDescriptions && (
                            <div className="text-sm font-bold text-gray-800">
                                {nbrSyncedResourcesDescriptions}
                            </div>
                        )}
                    {lastSyncDescription && (
                        <div className="text-sm text-gray-800">
                            {lastSyncDescription}
                        </div>
                    )}
                </div>

                {lastSyncFailedUnknownError() && (
                    <div className="text-red text-sm">
                        {i18next.t(
                            'settings.social_integration.failed_synchronisation',
                        )}
                    </div>
                )}

                {lastSyncFailedUnsupportedAccount() && (
                    <div className="text-red text-sm">
                        {i18next.t(
                            'settings.social_integration.failed_sync_unsupported_account',
                        )}
                    </div>
                )}
            </SettingsBlock>
        );
    };
    const getNewSyncTypes = (syncType: string, shouldSync: boolean) => {
        if (shouldSync) {
            return {
                syncTypes: socialIntegrationSettings
                    .get('syncTypes', List())
                    .push(syncType),
            };
        } else {
            const currentSyncTypes = socialIntegrationSettings.get(
                'syncTypes',
                List(),
            );
            const newSyncTypes = currentSyncTypes
                ? currentSyncTypes.filter((v) => v !== syncType)
                : List();
            return { syncTypes: newSyncTypes };
        }
    };

    const handleToggleChild = (syncType: string, value: boolean) => {
        const returnUrl = encodeURIComponent(
            createReturnUrl(extensionKey, syncSource),
        );
        const newSyncTypes = getNewSyncTypes(syncType, value);
        handleToggleDispatch(syncSource, returnUrl, newSyncTypes);
    };

    const handleToggleDispatch = async (
        syncSource: string,
        returnUrl: string,
        newSyncTypes: object,
    ) => {
        dispatch(externalSyncIsDone(false));
        // @ts-expect-error
        const result: { authorizationUrl?: string } = await dispatch(
            changeSocialIntegrationSettings(
                syncSource,
                returnUrl,
                newSyncTypes,
            ),
        );

        if (result.authorizationUrl) {
            goToExternalSignIn(
                dispatch,
                result.authorizationUrl,
                syncSource,
                extensionKey,
            );
        } else {
            try {
                dispatch(fetchSocialIntegrationSettings());
            } catch {
                console.error('Could not fetch social integration settings');
            }
        }

        setTimeout(() => {
            dispatch(externalSyncIsDone(true));
            dispatch(fetchSocialIntegrationSettings());
        }, 1000);
    };

    const otherCalendarIsActive = (): boolean =>
        !calendarToggled() && hasCalendarSync(socialIntegrationSettingValues);

    const syncInProgress = socialIntegrationSettings.get(
        'clientAuthInProgress',
        false,
    );

    const handleToggleAutomeeting = (value: boolean) => {
        setFetchCalendarTime(FETCH_CALENDAR_START_TIME);
        dispatch(toggleAutomeetingAction(extensionKey, calendar, value));
    };

    const shouldDisableAutomeeting = () =>
        !(calendarToggled() && calendar.has('url'));

    return (
        <Page
            header={SourceConfiguration[syncSource].title}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.account'], { extensionKey }),
                )
            }
        >
            {socialIntegrationSettings.has('error') ? (
                <SettingsBlock>
                    <div className="text-red text-xs">
                        {i18next.t(
                            'settings.social_integration.error_unavailable',
                        )}
                    </div>
                </SettingsBlock>
            ) : (
                <div>
                    {renderSynchronisationSpec()}

                    <SettingSection className="mt-3">
                        {i18next.t('settings.social_integration.what')}
                    </SettingSection>

                    {hasCapabilityContactSync && (
                        <SettingRow
                            cta={
                                <Toggle
                                    onChange={(v) =>
                                        handleToggleChild('contacts', v)
                                    }
                                    value={socialIntegrationSettings
                                        .get('syncTypes')
                                        .includes('contacts')}
                                    disabled={syncInProgress}
                                    loading={!syncIsDone}
                                    text={i18next.t(
                                        'settings.social_integration.synctypes.contacts',
                                    )}
                                />
                            }
                        >
                            {i18next.t(
                                'settings.social_integration.synctypes.contacts',
                            )}
                        </SettingRow>
                    )}

                    <Divider className="my-0" />

                    {canUseExternalCalendar(
                        calendar.getIn([
                            'configurationDTO',
                            'allowedCalendarSources',
                        ]),
                    ) &&
                        hasCapabilityCalendarSync && (
                            <SettingRow
                                cta={
                                    <Toggle
                                        onChange={(v) =>
                                            handleToggleChild('calendar', v)
                                        }
                                        value={calendarToggled()}
                                        disabled={
                                            syncInProgress ||
                                            otherCalendarIsActive()
                                        }
                                        loading={!syncIsDone}
                                        text={i18next.t(
                                            'settings.social_integration.synctypes.calendar',
                                        )}
                                    />
                                }
                            >
                                <div className="gap-2">
                                    {i18next.t(
                                        'settings.social_integration.synctypes.calendar',
                                    )}

                                    {otherCalendarIsActive() ? (
                                        <div className="text-sm text-gray-400">
                                            {i18next.t(
                                                'settings.social_integration.calendar_multiple',
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            </SettingRow>
                        )}

                    <Divider className="my-0" />

                    {hasCapabilityCalendarSync &&
                        SourceConfiguration[syncSource].workHourSync && (
                            <SettingRow
                                cta={
                                    <Toggle
                                        onChange={(v) =>
                                            handleToggleChild(
                                                'working_hours',
                                                v,
                                            )
                                        }
                                        value={socialIntegrationSettings
                                            .get('syncTypes')
                                            .includes('working_hours')}
                                        disabled={syncInProgress}
                                        loading={!syncIsDone}
                                        text={i18next.t('settings.workhours')}
                                    />
                                }
                            >
                                {i18next.t('settings.workhours')}
                            </SettingRow>
                        )}

                    {SourceConfiguration[syncSource].cloudSync && (
                        <SettingRow
                            cta={
                                <Toggle
                                    onChange={(v) =>
                                        handleToggleChild('cloud_files', v)
                                    }
                                    value={socialIntegrationSettings
                                        .get('syncTypes')
                                        .includes('cloud_files')}
                                    disabled={syncInProgress}
                                    loading={syncIsDone}
                                    text={i18next.t(
                                        'settings.social_integration.synctypes.google_drive',
                                    )}
                                />
                            }
                        >
                            {i18next.t(
                                'settings.social_integration.synctypes.google_drive',
                            )}
                        </SettingRow>
                    )}

                    <Divider className="my-0" />

                    <div className="py-7">
                        <SettingsBlock
                            className={twMerge(
                                shouldDisableAutomeeting() ? 'opacity-50' : '',
                            )}
                        >
                            <SettingSection className="mt-3">
                                {i18next.t(
                                    'settings.social_integration.calendar_settings',
                                )}
                            </SettingSection>

                            <div
                                className="p-2 text-sm text-gray-600"
                                dangerouslySetInnerHTML={{
                                    __html: i18next.t(
                                        'settings.social_integration.calendar_activate_info',
                                    ),
                                }}
                            />
                        </SettingsBlock>

                        <Divider />

                        <SettingRow
                            cta={
                                <Toggle
                                    onChange={handleToggleAutomeeting}
                                    value={
                                        shouldDisableAutomeeting()
                                            ? false
                                            : calendar.get('autoMeeting')
                                    }
                                    disabled={shouldDisableAutomeeting()}
                                    text={i18next.t(
                                        'calendar.other.external.automeeting',
                                    )}
                                />
                            }
                        >
                            {i18next.t('calendar.other.external.automeeting')}
                        </SettingRow>

                        <div className="p-2 text-sm text-gray-600">
                            {i18next.t(
                                'calendar.other.external.automeeting_socialintegration_info',
                            )}
                        </div>

                        <Divider />
                    </div>

                    <Button onClick={signOut} className="my-3" color="danger">
                        {i18next.t('settings.social_integration.sign_out')}
                    </Button>
                </div>
            )}
        </Page>
    );
};

export default SocialIntegrationSynchronisation;
